import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve session ID from localStorage
const getSessionId = () => {
  let sessionId = localStorage.getItem("sib_session_id");
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem("sib_session_id", sessionId);
  }
  return sessionId;
};

const AccessContext = createContext(null);

export function AccessProvider({ children }) {
  const [sessionId] = useState(getSessionId);
  const [email, setEmail] = useState(localStorage.getItem("sib_user_email") || null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAccessStatus = useCallback(async () => {
    try {
      const params = new URLSearchParams({ session_id: sessionId });
      if (email) params.append("email", email);
      
      const response = await axios.get(`${API}/access-status?${params}`);
      setAccessStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch access status:", error);
      // Default to full access on error
      setAccessStatus({
        mode: "full",
        access_level: "full",
        remaining_uses: null,
        daily_limit: null,
        free_tones: ["kind", "calm", "professional", "apologetic", "boundary", "friendly"],
        all_tones: ["kind", "calm", "professional", "apologetic", "boundary", "friendly"],
        free_relationships: ["general", "romantic", "family", "friend", "work"],
        all_relationships: ["general", "romantic", "family", "friend", "work"],
        is_whitelisted: false,
        has_redeemed_code: false,
        reviewer_badge: false
      });
    } finally {
      setLoading(false);
    }
  }, [sessionId, email]);

  useEffect(() => {
    fetchAccessStatus();
  }, [fetchAccessStatus]);

  const redeemCode = async (code) => {
    try {
      const response = await axios.post(`${API}/redeem-code?session_id=${sessionId}`, {
        code: code.trim()
      });
      
      if (response.data.success) {
        // Refresh access status after successful redemption
        await fetchAccessStatus();
        return { success: true, message: response.data.message };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || "Failed to redeem code" 
      };
    }
  };

  const setUserEmail = (newEmail) => {
    if (newEmail) {
      localStorage.setItem("sib_user_email", newEmail);
    } else {
      localStorage.removeItem("sib_user_email");
    }
    setEmail(newEmail);
  };

  const value = {
    sessionId,
    email,
    setUserEmail,
    accessStatus,
    loading,
    redeemCode,
    refreshAccess: fetchAccessStatus,
    // Convenience helpers
    isFullMode: accessStatus?.mode === "full",
    isLimitedMode: accessStatus?.mode === "limited",
    hasFullAccess: accessStatus?.access_level === "full" || accessStatus?.access_level === "reviewer",
    isReviewer: accessStatus?.reviewer_badge === true,
    remainingUses: accessStatus?.remaining_uses,
    freeTones: accessStatus?.free_tones || [],
    allTones: accessStatus?.all_tones || [],
    freeRelationships: accessStatus?.free_relationships || [],
    allRelationships: accessStatus?.all_relationships || []
  };

  return (
    <AccessContext.Provider value={value}>
      {children}
    </AccessContext.Provider>
  );
}

export function useAccess() {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccess must be used within an AccessProvider");
  }
  return context;
}
